import * as React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import styled from '@emotion/styled'
import { ClassyFooter, PageHeader, BREAKPOINTS, OpenInNew } from '@classy/campaign-page-blocks'
import { MemberDropdown } from 'components/MemberDropdown'
import { SsoSdk } from 'components/SsoSdk'
import { useSsoMember } from 'hooks/useSsoMember'
import { useTranslation } from 'next-i18next'

const StyledButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 56px;
  width: 100%;
  padding: 12px 24px;
  gap: 4px;
  border-radius: 8px;
  background: var(--theme-color__brand-tertiary);
  color: white;
  text-decoration: none;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    width: fit-content;
  }
`

const StyledParagraph = styled.p`
  font-size: var(--theme-font__paragraph-lg--font-size);
  font-weight: var(--theme-font__paragraph-lg--font-weight);
  line-height: 32px;
  margin: 0;
`

const StyledContainer = styled.div`
  background-color: var(--theme-color__background-accent);
  color: var(--theme-color__text-general);
  font-family: var(--theme-font__font-family--general);
  text-align: left;
  padding: 40px 24px;
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  min-height: 0;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    padding: 64px;
    text-align: center;
  }
`

const StyledBody = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: fit-content;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    max-width: 624px;
  }

  @media (min-width: ${BREAKPOINTS.desktop}px) {
    max-width: 752px;
  }
`

interface ErrorPageLayoutProps {
  title: string
  message: string
  dataId?: string
}

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

export const ErrorPageLayout: React.FC<ErrorPageLayoutProps> = ({
  title,
  message,
  dataId = 'cp-footer-error',
}) => {
  const { t } = useTranslation('common')
  const [isSsoSdkLoaded, setIsSsoSdkLoaded] = React.useState(false)
  const member = useSsoMember(isSsoSdkLoaded)

  return (
    <>
      <Head>
        <link rel="icon" href="/favicon.ico?v=3" sizes="any" />
        <title>{title}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <LayoutWrapper>
        <PageHeader hidePlaceholder>
          {member && <MemberDropdown member={member} />}
          <SsoSdk onReady={() => setIsSsoSdkLoaded(true)} />
        </PageHeader>
        <StyledContainer>
          <StyledBody>
            <h1>{title}</h1>
            <StyledParagraph>{message}</StyledParagraph>
            <StyledButton href="https://support.classy.org/s/" target="_blank">
              <OpenInNew fill="currentcolor" />
              <span>{t('404.helpCenter', 'Help center')}</span>
            </StyledButton>
          </StyledBody>
        </StyledContainer>
        <footer>
          <ClassyFooter
            data-id={dataId}
            privacyLinkUrl="https://www.gofundme.com/c/privacy"
            termsLinkUrl="https://www.gofundme.com/c/terms"
          />
        </footer>
      </LayoutWrapper>
    </>
  )
}
